import { Component, Inject, OnInit, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { SHARED_IMPORTS } from '../../shared/shared-imports';
import { ContentService } from '../../../services/content.service';

interface Tenant {
	TenantId: string;
	TenantName: string;
}

interface Unit {
	SerialNumber: string;
	MacAddress: string;
}

@Component({
	selector: 'global-unit',
	standalone: true,
	imports: [
		...SHARED_IMPORTS,
		MatSelectModule,
		MatOptionModule,
		MatButtonModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		MatListModule
	],
	templateUrl: 'components/global-admin/global-unit/global-unit.component.html',
	styleUrls: ['components/global-admin/global-unit/global-unit.component.css'],
	schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class GlobalUnitComponent implements OnInit {
	public filterUnit: string = '';
	public listOfUnitsToMove: Unit[] = [];
	public homeTenantSet: boolean = false;
	public listOfMovedUnits: string = '';
	public enableButton: boolean = false;

	public homeTenant: Tenant | null = null;
	public homeTenantId: number = -1;
	public nonHomeTenants: Tenant[] = [];
	public unitsByHomeTenant: Unit[] = [];
	public listOfUnitsHomeTenant: Unit[] = [];
	public currentTenant: string = '';
	public movedToTenant: string = '';
	public currentTenantFrom: string = '';
	public movedToTenantFrom: string = '';
	public unitFrom: Unit | null = null;
	public unit: Unit | null = null;
	public targetTenant: string = '';
	public movedSerialNumber: string = '';
	public previousTenant: string = '';
	public movedSerialNumberFrom: string = '';
	public previousTenantFrom: string = '';
	public targetTenantFrom: string = '';
	public unitsBySpecifiedTenant: Unit[] = [];
	public searchText: string = '';
	public searchTextFrom: string = '';
	public filteredUnits: Unit[] = [];
	public showConfirmMessageFrom: boolean = false;
	
	// UI state
	public showMoveToCustomer: boolean = true;
	public showMoveFromCustomer: boolean = false;
	public showConfirmMessage: boolean = false;
	public selectedUnitsToAdd: Unit[] = [];
	public selectedUnitsToRemove: Unit[] = [];

	constructor(
		@Inject(HttpClient) private http: HttpClient,
		@Inject(ContentService) private contentService: ContentService
	) {
		// Load from localStorage
		const savedFilter = localStorage.getItem('globalUnit.filterUnit');
		if (savedFilter) this.filterUnit = savedFilter;
	}

	ngOnInit(): void {
		this.getHomeTenant();
		this.getHomeTenantId();
		this.getNonHomeTenants();
		this.getUnitsByHomeTenant();
		this.checkIfUnitSelected();
	}

	private checkIfHomeTenantSet(): void {
		this.homeTenantSet = this.homeTenantId !== -1;
	}

	public getHomeTenant(): void {
		this.http.get<Tenant>('/api/Tenant/GetDefaultHomeTenant').subscribe(tenant => {
			this.homeTenant = tenant;
		});
	}

	public getHomeTenantId(): void {
		this.http.get<number>('/api/Tenant/GetDefaultHomeTenantId').subscribe(tenantId => {
			this.homeTenantId = tenantId;
			this.checkIfHomeTenantSet();
		});
	}

	public getNonHomeTenants(): void {
		this.http.get<Tenant[]>('/api/Tenant/GetNonDefaultHomeTenants').subscribe(tenants => {
			this.nonHomeTenants = tenants;
		});
	}

	public getUnitsByHomeTenant(): void {
		this.http.get<Unit[]>('/api/Unit/GetUnitsByDefaultHomeTenant').subscribe(units => {
			this.unitsByHomeTenant = units;
			this.listOfUnitsHomeTenant = [...units];
			this.updateFilteredUnits();
		});
	}

	public getTenant(serialNumber: string): void {
		this.http.get<string>(`/api/Unit/GetTenantForUnit?serialNumber=${serialNumber}`).subscribe(tenant => {
			this.currentTenant = tenant;
		});
	}

	public getTargetTenant(serialNumber: string): void {
		this.http.get<string>(`/api/Unit/GetTenantForUnit?serialNumber=${serialNumber}`).subscribe(tenant => {
			this.movedToTenant = tenant;
		});
	}

	public getTenantFrom(): void {
		if (this.unitFrom) {
			this.http.get<string>(`/api/Unit/GetTenantForUnit?serialNumber=${this.unitFrom.SerialNumber}`).subscribe(tenant => {
				this.currentTenantFrom = tenant;
			});
		}
	}

	public getTargetTenantFrom(): void {
		if (this.unitFrom) {
			this.http.get<string>(`/api/Unit/GetTenantForUnit?serialNumber=${this.unitFrom.SerialNumber}`).subscribe(tenant => {
				this.movedToTenantFrom = tenant;
			});
		}
	}

	public addUnits(): void {
		// Move selected units from available to selected
		this.selectedUnitsToAdd.forEach(unit => {
			if (!this.listOfUnitsToMove.includes(unit)) {
				this.listOfUnitsToMove.push(unit);
				const index = this.listOfUnitsHomeTenant.indexOf(unit);
				if (index > -1) {
					this.listOfUnitsHomeTenant.splice(index, 1);
				}
			}
		});
		this.selectedUnitsToAdd = [];
		this.updateFilteredUnits();
		this.checkIfUnitSelected();
		if (this.listOfUnitsToMove.length > 0) {
			this.getTenant(this.listOfUnitsToMove[0].SerialNumber);
		}
	}

	public removeUnits(): void {
		// Move selected units from selected back to available
		this.selectedUnitsToRemove.forEach(unit => {
			if (this.listOfUnitsToMove.includes(unit)) {
				this.listOfUnitsHomeTenant.push(unit);
				const index = this.listOfUnitsToMove.indexOf(unit);
				if (index > -1) {
					this.listOfUnitsToMove.splice(index, 1);
				}
			}
		});
		this.selectedUnitsToRemove = [];
		this.updateFilteredUnits();
		this.checkIfUnitSelected();
	}

	public getListOfMovedUnits(): void {
		this.listOfMovedUnits = this.listOfUnitsToMove.map(u => u.SerialNumber).join(", ");
	}

	public moveUnitBetweenTenants(): void {
		if (this.listOfUnitsToMove.length > 0) {
			for (const unit of this.listOfUnitsToMove) {
				this.moveUnit(unit.SerialNumber, unit.MacAddress);
			}
		} else if (this.unit) {
			this.moveUnit(this.unit.SerialNumber, this.unit.MacAddress);
		}
	}

	private moveUnit(serialNumber: string, macAddress: string): void {
		this.http.post(`/api/Iot/MoveUnit?tenantId=${this.targetTenant}&serialNumber=${serialNumber}&macAddress=${macAddress}&allowReassignment=true`, {})
			.subscribe(() => {
				this.getTargetTenant(serialNumber);
				this.getUnitsByHomeTenant();
				this.getListOfMovedUnits();
				this.movedSerialNumber = serialNumber;
				this.previousTenant = this.currentTenant;
				this.showConfirmMessage = true;
				this.clearAutocomplete();
				this.targetTenant = '';
				this.listOfUnitsToMove = [];
				this.listOfMovedUnits = '';
			});
	}

	public moveUnitFromCustomer(): void {
		if (!this.unitFrom) return;
		
		this.http.post(`/api/Iot/MoveUnit?tenantId=${this.homeTenantId}&serialNumber=${this.unitFrom.SerialNumber}&macAddress=${this.unitFrom.MacAddress}&allowReassignment=true`, {})
			.subscribe(() => {
				this.movedSerialNumberFrom = this.unitFrom!.SerialNumber;
				this.getTargetTenantFrom();
				this.getTenantFrom();
				this.previousTenantFrom = this.currentTenantFrom;
				this.showConfirmMessageFrom = true;
				this.populateUnits();
				this.clearAutocompleteFrom();
				this.targetTenantFrom = '';
			});
	}

	public checkIfUnitSelected(): void {
		this.enableButton = this.listOfUnitsToMove.length > 0;
	}

	public populateUnits(): void {
		this.clearAutocompleteFrom();
		this.http.get<Unit[]>(`/api/Unit/GetUnitsBySpecifiedTenant?tenantId=${this.targetTenantFrom}`)
			.subscribe(units => {
				this.unitsBySpecifiedTenant = units;
			});
	}

	public showMoveToUnitView(): void {
		this.showMoveToCustomer = true;
		this.showMoveFromCustomer = false;
		this.ngOnInit();
		this.showConfirmMessage = false;
	}

	public showMoveFromUnitView(): void {
		this.showMoveFromCustomer = true;
		this.showMoveToCustomer = false;
		this.ngOnInit();
		this.populateUnits();
		this.showConfirmMessageFrom = false;
	}

	public clearAutocomplete(): void {
		this.searchText = '';
	}

	public clearAutocompleteFrom(): void {
		this.searchTextFrom = '';
		this.unitFrom = null;
	}

	public getSuccessMessage(): string {
		return this.contentService.getContent("MoveUnitBetweenTenants.UnitSuccessfullyMoved", this.movedSerialNumber, this.previousTenant, this.movedToTenant);
	}

	public getSuccessMessageFrom(): string {
		return this.contentService.getContent("MoveUnitBetweenTenants.UnitSuccessfullyMoved", this.movedSerialNumberFrom, this.previousTenantFrom, this.movedToTenantFrom);
	}

	public doFilter(): void {
		this.filterUnit = this.filterUnit.toLowerCase();
		localStorage.setItem('globalUnit.filterUnit', this.filterUnit);
		this.updateFilteredUnits();
	}

	public showRecord(record: Unit): boolean {
		return record && record.SerialNumber?.toLowerCase().includes(this.filterUnit.toLowerCase());
	}

	public updateFilteredUnits(): void {
		this.filteredUnits = this.listOfUnitsHomeTenant.filter(unit => this.showRecord(unit));
	}

	public onUnitFromChange(): void {
		if (this.unitFrom) {
			this.getTenantFrom();
		}
	}
}